import { scaleLinear, scaleOrdinal } from "@visx/scale";
import { useMemo } from "react";
import {
  MULTICOLOUR_AREA_COLOURS,
  MULTICOLOUR_LINE_COLOURS,
} from "../constants";
import type {
  Data,
  ScaleFunctionsProps,
  TimeScaleFunctionProps,
} from "../types";
import { getBandScaleHelpers, getTimeScaleHelpers } from "./util";

export const useXScale = ({
  xMax,
  data,
  comparator,
  bottomAccessor,
  variant = "line",
  tickRenderer,
  from,
  to,
  interval,
}:
  | ScaleFunctionsProps
  | TimeScaleFunctionProps): any /* IMPROVE: TS2742 workaround */ => {
  const commonProps = {
    xMax,
    data,
    comparator,
    bottomAccessor,
    tickRenderer,
    variant,
  };
  return useMemo(() => {
    if (from && to) {
      return getTimeScaleHelpers({ ...commonProps, from, to, interval });
    } else {
      return getBandScaleHelpers(commonProps);
    }
  }, [xMax, data, comparator, bottomAccessor]);
};

export const useYScale = ({
  yMax,
  data,
  maxThreshold,
  hasBars,
}: {
  yMax: number;
  data: number[];
  maxThreshold?: number;
  hasBars?: boolean;
}): any /* IMPROVE: TS2742 workaround */ => {
  const args = [...data, ...(maxThreshold ? [maxThreshold] : [])];

  const min = Math.min(...args);

  const minDomain = min * (min < 0 ? 1.05 : 0.95);

  const max = Math.max(...args);

  const maxDomain = max + min * (max < 0 ? -0.05 : 0.05);

  return useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        round: true,
        domain: [minDomain, maxDomain],
      }),
    [yMax, data, hasBars]
  );
};

export const useColourScale = ({
  sets,
  variant = "line",
}: {
  sets: Data;
  variant?: "line" | "area";
}): any /* IMPROVE: TS2742 workaround */ => {
  return useMemo(
    () =>
      scaleOrdinal({
        domain: sets.map(({ name }) => name) as Data,
        range:
          variant === "line"
            ? MULTICOLOUR_LINE_COLOURS
            : MULTICOLOUR_AREA_COLOURS,
      }),
    []
  );
};
